import { useMemo } from "react";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import useClocksRemindersSummary from "components/hooks/useClocksRemindersSummary";

const flatCardSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "surface.card",
  backgroundImage: "none",
  borderRadius: "20px",
} as const;

function AvgUniqueKlydosWidget() {
  const { data, isLoading, error } = useClocksRemindersSummary();

  const stats = useMemo(() => {
    if (!data || data.length === 0) return null;
    let totalUnique = 0;
    let clocksCounted = 0;
    for (const entry of data) {
      const uniqueKlydos = Object.entries(entry.clockReminders).reduce(
        (acc, [, reminders]) => (reminders?.length ? acc + 1 : acc),
        0,
      );
      if (uniqueKlydos === 0) continue;
      totalUnique += uniqueKlydos;
      clocksCounted++;
    }
    if (clocksCounted === 0) return null;
    return {
      avg: totalUnique / clocksCounted,
      totalUnique,
      clocksCounted,
    };
  }, [data]);

  return (
    <DSCard sx={flatCardSx}>
      <DSCardContent sx={{ flex: 1 }}>
        <DSBox
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1,
          }}
        >
          <DSBox sx={{ typography: "body2", color: "text.secondary" }}>
            Avg Unique Klydos w/ Reminders per Clock
          </DSBox>
          <DSBox
            sx={{
              color: "surface.accentText",
              "& svg": { fontSize: "1.5rem" },
              display: "flex",
            }}
          >
            <InsightsOutlinedIcon />
          </DSBox>
        </DSBox>
        <DSBox sx={{ typography: "h6", fontFamily: "Gilroy-Bold" }}>
          {isLoading ? (
            <DSCircularProgress size={20} />
          ) : error ? (
            <DSTypography variant="body2" sx={{ color: "error.main" }}>
              Failed to load
            </DSTypography>
          ) : !stats ? (
            <DSTypography variant="body2" sx={{ color: "text.secondary" }}>
              No reminders set
            </DSTypography>
          ) : (
            stats.avg.toFixed(2)
          )}
        </DSBox>
        {stats && (
          <DSTypography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, fontSize: 12 }}
          >
            {stats.totalUnique} unique pairings across {stats.clocksCounted}{" "}
            clocks
          </DSTypography>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default AvgUniqueKlydosWidget;
